import axios from "axios";
import { ENDPOINTS } from "constants/endpoints";
import { toastBackendError } from "utilities/toastBackendError";

export const handleFetchKybCountries = async () => {
    try {
        const response = await axios.get(ENDPOINTS.getKybCountries);
        return response?.data;
    } catch (error) {
        toastBackendError(error, "Unable to load countries");
        return error?.response?.data || { message: "Unable to load countries" };
    }
};

export const handleFetchKybAvailableWallets = async () => {
    try {
        const response = await axios.get(ENDPOINTS.getKybAvailableWallets);
        return response?.data;
    } catch (error) {
        toastBackendError(error, "Unable to load available wallets");
        return error?.response?.data || { message: "Unable to load available wallets" };
    }
};

export const handleCreateBusiness = async (data) => {
    try {
        const response = await axios.post(ENDPOINTS.createBusiness, data);
        return response?.data;
    } catch (error) {
        const message = toastBackendError(error, "An error occurred");
        throw new Error(message);
    }
};

export const handleCheckPhoneVerification = async (countryCode) => {
    try {
        const response = await axios.get(ENDPOINTS.checkPhoneVerification(countryCode));
        return response?.data;
    } catch (error) {
        toastBackendError(error, "Unable to check phone verification");
        return error?.response?.data || { message: "Unable to check phone verification" };
    }
};

export const handleUpdateBusinessAddress = async (businessId, data) => {
    try {
        const response = await axios.patch(ENDPOINTS.updateBusinessAddress(businessId), data);
        return response?.data;
    } catch (error) {
        const message = toastBackendError(error, "Unable to update contact details");
        throw new Error(message);
    }
};

export const handleUpdateBusinessDocuments = async (businessId, data) => {
    try {
        const response = await axios.patch(ENDPOINTS.updateBusinessDocuments(businessId), data);
        return response?.data;
    } catch (error) {
        const message = toastBackendError(error, "Unable to update business documents");
        throw new Error(message);
    }
};

export const handleUpdateBusinessFinancialInformation = async (businessId, data) => {
    try {
        const response = await axios.patch(
            ENDPOINTS.updateBusinessFinancialInformation(businessId),
            data
        );
        return response?.data;
    } catch (error) {
        const message = toastBackendError(error, "Unable to update financial information");
        throw new Error(message);
    }
};

export const handleAddBusinessUbo = async (businessId, data) => {
    try {
        const response = await axios.post(ENDPOINTS.addBusinessUbo(businessId), data);
        return response?.data;
    } catch (error) {
        const message = toastBackendError(error, "Unable to add control person");
        throw new Error(message);
    }
};

export const handleAddBusinessDirector = async (businessId, data) => {
    try {
        const response = await axios.post(ENDPOINTS.addBusinessDirector(businessId), data);
        return response?.data;
    } catch (error) {
        const message = toastBackendError(error, "Unable to add director");
        throw new Error(message);
    }
};

export const handleRequestKybVerification = async () => {
    try {
        const response = await axios.post(ENDPOINTS.requestKybVerification);
        return response?.data;
    } catch (error) {
        const message = toastBackendError(error, "Unable to request verification");
        throw new Error(message);
    }
};

export const handleFetchBusinessById = async (businessId) => {
    try {
        const response = await axios.get(ENDPOINTS.getBusinessById(businessId));
        return response?.data;
    } catch (error) {
        const message = toastBackendError(error, "Unable to fetch business details");
        throw new Error(message);
    }
};

export const handleFetchKybRequirementsSummary = async (businessId) => {
    try {
        const response = await axios.get(ENDPOINTS.getKybRequirementsSummary(businessId));
        return response?.data;
    } catch (error) {
        const message = toastBackendError(error, "Unable to fetch requirements summary");
        throw new Error(message);
    }
};

export const handleUploadOnboardingDocument = async (file) => {
    try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(ENDPOINTS.uploadFileLink, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

        return response?.data;
    } catch (error) {
        const message = toastBackendError(error, "Unable to upload document");
        throw new Error(message);
    }
};

export const handleSendBusinessOtp = async (data) => {
    try {
        const response = await axios.post(ENDPOINTS.sendBusinessOtp, data);
        return response?.data;
    } catch (error) {
        const message = toastBackendError(error, "Unable to send OTP");
        throw new Error(message);
    }
};

export const handleVerifyBusinessOtp = async (data) => {
    try {
        const response = await axios.post(ENDPOINTS.verifyBusinessOtp, data);
        return response?.data;
    } catch (error) {
        const message = toastBackendError(error, "Unable to verify OTP");
        throw new Error(message);
    }
};

export const handleUpdateBusinessPhoneNumber = async (businessId, data) => {
    try {
        const response = await axios.patch(ENDPOINTS.updateBusinessPhoneNumber, data, {
            headers: {
                "x-business-id": businessId,
            },
        });
        return response?.data;
    } catch (error) {
        const message = toastBackendError(error, "Unable to update phone number");
        throw new Error(message);
    }
};

export const handleSwitchCurrentBusiness = async (businessId) => {
    try {
        const response = await axios.put(ENDPOINTS.switchCurrentBusiness(businessId));
        return response?.data;
    } catch (error) {
        const message = toastBackendError(error, "Unable to switch business");
        throw new Error(message);
    }
};

export const handleMarkAsViewedMaintenanceFeeInformation = async () => {
  try {
    const response = await axios.patch(ENDPOINTS.markMaintenanceFeeInformationViewed);
    return response?.data;
  } catch (error) {
    return error?.response?.data || { message: "Unable to mark as viewed" };
  }
};

export const handleGetActiveMaintenanceFees = async () => {
  try {
    const response = await axios.get(ENDPOINTS.getActiveMaintenanceFees);
    return response?.data;
  } catch (error) {
    return error?.response?.data || { message: "Unable to fetch active maintenance fees" };
  }
};
